import { useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useViewNavigate } from '../../hooks/useViewNavigate.js';
import { useEnglishLevel } from '../../screens/leveling/useEnglishLevel.js';
import { A0_LEGACY_APP_ENABLED } from '../../screens/a0/legacy.js';

/**
 * Bottom tab bar. Dos variantes según el nivel del usuario:
 *  - `full` (A1+): Inicio / Actividad / Biblioteca / Progreso / Perfil.
 *  - `a0` (principiante): Inicio / Ayuda / Perfil. Las superficies A1+ están
 *    gateadas (`A0Gated`) y, mostradas en la barra, eran botones-trampa que
 *    rebotaban a /home; el principiante no las tiene, y gana Ayuda (la que más
 *    necesita).
 *
 * La variante se DERIVA del nivel (`useEnglishLevel`), no se pasa por prop: así
 * TODA pantalla alcanzable por un A0 (Home, Perfil, Ayuda) muestra el footer
 * correcto sin que cada caller tenga que recordarlo (HelpScreen lo olvidaba).
 *
 * Active item is derived from the current route via `useLocation`. La
 * conversación NO tiene tab: se arranca desde el CTA hero de la Home (decisión
 * de diseño V3). Las secciones sin tab propio (/scenarios, /listen, /program)
 * no iluminan NINGÚN tab: antes caían al default 'home' y la barra decía
 * "Inicio" estando en otra sección. (/help SÍ ilumina, pero sólo en la variante
 * `a0`, donde es un tab.)
 */

type TabKey = 'home' | 'activity' | 'library' | 'stats' | 'profile' | 'help';

interface TabDef {
  key: TabKey;
  label: string;
  path: string;
  icon: ReactNode;
}

const ICON_PROPS = {
  width: 22,
  height: 22,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

const TABS: readonly TabDef[] = [
  {
    key: 'home',
    label: 'Inicio',
    path: '/home',
    icon: (
      <svg {...ICON_PROPS}>
        <path d="M3 10.5 12 3l9 7.5" />
        <path d="M5 9.5V20a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V9.5" />
      </svg>
    ),
  },
  {
    key: 'activity',
    label: 'Actividad',
    path: '/activity',
    icon: (
      <svg {...ICON_PROPS}>
        <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
        <path d="M3 3v5h5" />
        <path d="M12 7v5l4 2" />
      </svg>
    ),
  },
  {
    key: 'library',
    label: 'Biblioteca',
    path: '/library',
    icon: (
      <svg {...ICON_PROPS}>
        <rect x="3" y="4" width="5" height="16" rx="1.4" />
        <rect x="10" y="4" width="5" height="16" rx="1.4" />
        <path d="M17.5 5 L 21 18 a 1 1 0 0 1 -0.7 1.2 L 18 19.8" />
      </svg>
    ),
  },
  {
    key: 'stats',
    label: 'Progreso',
    path: '/progress',
    icon: (
      <svg {...ICON_PROPS}>
        <path d="M5 21V10" />
        <path d="M12 21V4" />
        <path d="M19 21v-7" />
      </svg>
    ),
  },
  {
    key: 'profile',
    label: 'Perfil',
    path: '/profile',
    icon: (
      <svg {...ICON_PROPS}>
        <circle cx="12" cy="8" r="3.5" />
        <path d="M5 20a7 7 0 0 1 14 0" />
      </svg>
    ),
  },
];

const HELP_TAB: TabDef = {
  key: 'help',
  label: 'Ayuda',
  path: '/help',
  icon: (
    <svg {...ICON_PROPS}>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.6 9.2a2.5 2.5 0 0 1 4.4 1.4c0 1.6-2 1.9-2.2 3.4" />
      <path d="M12 17.2h.01" />
    </svg>
  ),
};

/** Footer del principiante A0: Inicio · Ayuda · Perfil (índices de TABS). */
const A0_TABS: readonly TabDef[] = [TABS[0]!, HELP_TAB, TABS[4]!];

function activeKeyFor(pathname: string): TabKey | null {
  if (pathname.startsWith('/home')) return 'home';
  if (pathname.startsWith('/activity')) return 'activity';
  if (pathname.startsWith('/library')) return 'library';
  if (pathname.startsWith('/progress')) return 'stats';
  if (pathname.startsWith('/profile')) return 'profile';
  if (pathname.startsWith('/help')) return 'help'; // sólo es un tab en la variante a0
  // Secciones sin tab propio (/scenarios, /listen, /program): ningún tab activo
  // — la barra no miente ubicación.
  return null;
}

export function TabBar() {
  const location = useLocation();
  const navigate = useViewNavigate();
  const { state } = useEnglishLevel();
  const active = activeKeyFor(location.pathname);
  // La variante `a0` sólo existe con la app A0 encendida (plan 48).
  const tabs = A0_LEGACY_APP_ENABLED && state?.cefrGlobal === 'A0' ? A0_TABS : TABS;

  return (
    <nav className="tabbar" aria-label="Navegación principal">
      {tabs.map((tab) => (
        <button
          key={tab.key}
          type="button"
          className={`tab${tab.key === active ? ' active' : ''}`}
          data-nav={tab.key}
          aria-current={tab.key === active ? 'page' : undefined}
          onClick={() => navigate(tab.path)}
        >
          {tab.icon}
          <span>{tab.label}</span>
        </button>
      ))}
    </nav>
  );
}
